import { parseISO } from 'date-fns';
import { saveAs } from 'file-saver';

import {
  ProvisioningRequestDto,
  ProvisioningRequestsDto,
} from './bulkProvisioningApi.interface.ts';
import { authFetch } from 'auth/utils.ts';
import { ProvisioningSourceFilter } from '../constants/provisioningSource.ts';
import {
  ProvisioningStatusFilter,
  PROVISIONING_STATUS,
  PROVISIONING_STATUS_TO_STATUS_LABEL_MAP,
} from '../constants/provisioningStatus.ts';

import { isEmpty } from 'lodash';
import { envVariables } from 'common/utils/envVariables.ts';
import { getFileNameFromContentDispositionHeader } from 'common/utils/fileDownload.ts';
import { ObjectValues } from 'common/tsHelpers.ts';

export const TEMPLATES = {
  MASTER: 'MasterTemplate',
  BULK_PROVISIONING: 'BulkProvisioning',
  UNIT_DATA_ADJUSTMENT: 'UnitDataAdjustment',
  SIM_PROFILE_MANAGEMENT: 'SimProfileManagement',
} as const;

export type TemplateType = ObjectValues<typeof TEMPLATES>;
export const isTemplateType = (x: any): x is TemplateType => Object.values(TEMPLATES).includes(x);

type FetchBulkProvisioningJobsProps = {
  page: number;
  jobsPerRow: number;
  status: ProvisioningStatusFilter;
  source: ProvisioningSourceFilter;
  uploadDateFrom: Date | null;
  uploadDateTo: Date | null;
  fileName: string;
  orderNumber: string;
};

export const fetchBulkProvisioningJobs = async (
  {
    page,
    jobsPerRow,
    status,
    source,
    uploadDateFrom,
    uploadDateTo,
    fileName,
    orderNumber,
  }: FetchBulkProvisioningJobsProps,
  signal?: AbortSignal,
) => {
  const urlQuery = new URLSearchParams({
    PageNumber: (page + 1).toString(),
    PageSize: jobsPerRow.toString(),
  });

  urlQuery.append('FileName', fileName);

  if (status !== '') {
    urlQuery.append('Statuses', [status].toString());
  }

  if (!isEmpty(source)) {
    source.forEach((source) => urlQuery.append('Sources', source.toString()));
  }

  if (uploadDateFrom) {
    urlQuery.append('UploadDateFrom', uploadDateFrom.toISOString());
  }

  if (uploadDateTo) {
    urlQuery.append('UploadDateTo', uploadDateTo.toISOString());
  }

  if (orderNumber) {
    urlQuery.append('OrderNumber', orderNumber);
  }

  const bulkProvisioningJobsResponse = await authFetch(
    `${envVariables.VITE_BULK_PROVISIONING_PROVISIONING_REQUESTS_API}?` + urlQuery.toString(),
    { signal },
  );

  if (!bulkProvisioningJobsResponse.ok) {
    throw new Error(bulkProvisioningJobsResponse.status.toString());
  }

  const bulkProvisioningJobsData: ProvisioningRequestsDto =
    await bulkProvisioningJobsResponse.json();

  return {
    items: bulkProvisioningJobsData.items.map(
      ({
        id,
        status,
        fileName,
        createdAt,
        modifiedAt,
        simStatusesCount: { failed, inProgress, succeed },
        source,
        allChunksReceived,
        errorSummaryFileName,
        orderNumber,
        uploaderName,
      }) => ({
        id,
        status: PROVISIONING_STATUS_TO_STATUS_LABEL_MAP[status],
        numberOfCards: allChunksReceived ? failed + inProgress + succeed : 0,
        numberOfSuccessCards: succeed,
        numberOfFailedCards: failed,
        fileName,
        uploadAt: parseISO(createdAt),
        uploadFinishedAt:
          status === PROVISIONING_STATUS.PROCESSING_SUCCEED ? parseISO(modifiedAt) : null,
        source: source,
        errorSummaryFileName: errorSummaryFileName,
        orderNumber,
        uploadBy: uploaderName,
      }),
    ),
    numberOfPages: bulkProvisioningJobsData.totalPages,
    totalNumberOfItems: bulkProvisioningJobsData.totalCount,
  };
};

export const uploadBulkProvisioningFile = async (file: File) => {
  const data = new FormData();
  data.append('file', file, file.name);
  const response = await authFetch(
    `${envVariables.VITE_BULK_PROVISIONING_PROVISIONING_REQUESTS_API}`,
    {
      method: 'POST',
      body: data,
    },
  );

  if (!response.ok) {
    await response.text().then((text) => {
      throw new Error(text);
    });
  }
};

export const fetchBulkProvisioningJob = async (id: string, signal?: AbortSignal) => {
  const bulkProvisioningJobsResponse = await authFetch(
    `${envVariables.VITE_BULK_PROVISIONING_PROVISIONING_REQUESTS_API}/${id}`,
    { signal },
  );

  const bulkProvisioningJobData: ProvisioningRequestDto = await bulkProvisioningJobsResponse.json();

  return {
    uploadFinishedAt:
      bulkProvisioningJobData.status === PROVISIONING_STATUS.PROCESSING_SUCCEED
        ? parseISO(bulkProvisioningJobData.modifiedAt)
        : null,
    uploadAt: parseISO(bulkProvisioningJobData.createdAt),
    status: PROVISIONING_STATUS_TO_STATUS_LABEL_MAP[bulkProvisioningJobData.status],
    id: bulkProvisioningJobData.id,
    fileName: bulkProvisioningJobData.fileName,
    numberOfCards: bulkProvisioningJobData.allChunksReceived
      ? bulkProvisioningJobData.simStatusesCount.failed +
        bulkProvisioningJobData.simStatusesCount.inProgress +
        bulkProvisioningJobData.simStatusesCount.succeed
      : 0,
    numberOfSuccessCards: bulkProvisioningJobData.simStatusesCount.succeed,
    numberOfFailedCards: bulkProvisioningJobData.simStatusesCount.failed,
  };
};

const downloadFile = async (filePath: string) => {
  const fileResponse = await authFetch(filePath);

  const filename = getFileNameFromContentDispositionHeader(
    fileResponse.headers.get('Content-Disposition'),
  );

  const blob = await fileResponse.blob();

  saveAs(blob, filename);
};

const downloadFileWithErrorHandling = async (filePath: string, fileName?: string) => {
  const fileResponse = await authFetch(filePath);

  if (!fileResponse.ok) {
    throw new Error(fileResponse.statusText);
  }

  const defaultFilename = getFileNameFromContentDispositionHeader(
    fileResponse.headers.get('Content-Disposition'),
  );

  const blob = await fileResponse.blob();

  saveAs(blob, fileName ?? defaultFilename);

  return fileResponse;
};

export const downloadErrorSummary = async (id: string) =>
  downloadFile(
    `${envVariables.VITE_BULK_PROVISIONING_PROVISIONING_REQUESTS_API}/${id}/error-summary`,
  );

export const downloadBulkProvisioningInputFile = async (id: string) =>
  downloadFile(`${envVariables.VITE_BULK_PROVISIONING_PROVISIONING_REQUESTS_API}/${id}/file`);

export const downloadBulkTemplate = async (type: TemplateType) => {
  await downloadFileWithErrorHandling(
    `${envVariables.VITE_BULK_PROVISIONING_PROVISIONING_REQUESTS_API}/templates/${type}`,
    type,
  );
};

export const downloadSummaryReport = async (date: string) => {
  const response = await authFetch(
    `${envVariables.VITE_BULK_PROVISIONING_PROVISIONING_REQUESTS_API}/activation-report`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        date,
      }),
    },
  );

  if (!response.ok) {
    throw new Error('fail to download');
  }

  return response;
};
